import { CalendarDays, Globe, Phone, Headphones, MessageSquare } from 'lucide-react';

export type CommunicationSource = "all" | "live-sms" | "receptionist" | "web-agent" | "online-booking" | "customer-chats" | "dispatcher-calls" | "tech-line";
export const communicationSources = [
  {
    id: 'all',
    title: 'All communications',
    provider: 'Live Twilio + ServiceTitan archive',
    icon: MessageSquare,
    description: 'One chronological inbox for live texts, imported messages, and calls.',
    requirements: [],
  },
  {
    id: 'live-sms',
    title: 'Live SMS inbox',
    provider: 'Twilio · Main company number',
    icon: MessageSquare,
    description: 'Read and reply to customer texts from WorkView.',
    requirements: [
      'Finish the Twilio number port and attach the number to its approved messaging campaign.',
      'Set Twilio incoming-message and delivery-status webhooks to WorkView during cutover.',
    ],
  },
  {
    id: 'receptionist',
    title: 'AI receptionist',
    provider: 'Twilio · Calls & texts',
    icon: Phone,
    description: 'Answer calls, save transcripts, find or create customers, and book into Dispatch.',
    requirements: [
      'Connect the company Twilio number for incoming calls and text messages.',
      'Connect the AI receptionist and save call transcripts to the customer conversation.',
      'Match existing customers or create a new customer and service location.',
      'Book against live Dispatch availability and recheck the slot before saving.',
      'Link each call to its booking, job, invoices, and payments for revenue tracking.',
    ],
  },
  {
    id: 'customer-chats',
    title: 'Customer chats',
    provider: 'Office · Customer conversations',
    icon: MessageSquare,
    description: 'Search and read ServiceTitan customer chat history, including messages and photos.',
    requirements: [
      'Connect customer chat and messaging channels to a shared customer history.',
      'Save incoming and outgoing messages with timestamps and the responding team member.',
      'Search conversations by customer, phone number, or message content.',
      'Link each conversation to its booking and job, with a saved review status.',
    ],
  },
  {
    id: 'dispatcher-calls',
    title: 'Dispatcher calls',
    provider: 'Primary company number · Staff answered',
    icon: Headphones,
    description: 'Review historical and live dispatcher calls, with recordings and job context.',
    requirements: [
      'Connect the primary company number and route incoming calls to the live dispatcher.',
      'Connect call recording and transcription for staff-answered calls.',
      'Save the caller number, answering dispatcher, call time, duration, and outcome.',
      'Match the caller to a customer and link the recording and transcript to the booking and job.',
      'Keep staff-answered calls distinct from AI-handled calls and save their review status.',
    ],
  },
  {
    id: 'tech-line',
    title: 'Tech line',
    provider: 'Twilio · Staff-to-staff',
    icon: Headphones,
    description: 'Review recorded technician and manager connections from the MGP Tech Line.',
    requirements: [],
  },
  {
    id: 'web-agent',
    title: 'Website agent',
    provider: 'Customer website · Chat',
    icon: Globe,
    description: 'Help website visitors, capture service details, and book from a conversation.',
    requirements: [
      'Add the WorkView chat widget to the customer’s website.',
      'Save the conversation and match or create the customer and service location.',
      'Offer available appointments using the same Dispatch scheduling rules.',
      'Link the chat to the booked job and its resulting revenue.',
    ],
  },
  {
    id: 'online-booking',
    title: 'Online booking form',
    provider: 'Customer website · Self-service',
    icon: CalendarDays,
    description: 'Let customers choose an available appointment and book directly into Dispatch.',
    requirements: [
      'Embed a booking form or link to it from the customer’s website.',
      'Collect customer, service address, and requested service details.',
      'Show live availability for the service duration and eligible technicians or teams.',
      'Recheck availability, create the booking, and send the customer a confirmation.',
      'Keep the online booking source attached to the job for revenue tracking.',
    ],
  },
] as const;

export function CommunicationIntegrations({ selected, onSelect }: { selected: CommunicationSource | null; onSelect: (source: CommunicationSource) => void }) {
  return <section className="comms-integrations" aria-label="Booking channels and integrations">
    <div className="comms-integration-grid">
      {communicationSources.filter(channel => !["all", "live-sms", "customer-chats"].includes(channel.id)).map(channel => {
        const Icon = channel.icon;
        return <button type="button" key={channel.id} className="comms-integration comms-source-card" aria-pressed={selected === channel.id} aria-expanded={selected === channel.id} aria-controls="comms-channel-widget" onClick={() => onSelect(channel.id)}>
          <span className="comms-integration-top"><Icon size={21} aria-hidden="true" /><span className="comms-integration-status">{channel.id === 'all' ? 'Unified inbox' : channel.id === 'dispatcher-calls' ? 'Live + archive' : channel.id === 'tech-line' ? 'Live' : channel.id === 'customer-chats' ? 'Archive connected' : channel.id === 'live-sms' ? 'Ready for cutover' : 'Needs integration'}</span></span>
          <span className="comms-source-title">{channel.title}</span>
          <span className="comms-integration-provider">{channel.provider}</span>
          <span className="comms-integration-description">{channel.description}</span>
          <span className="comms-source-open">{selected === channel.id ? 'Viewing activity' : channel.id === 'all' ? 'Open unified inbox' : channel.id === 'live-sms' ? 'Open SMS inbox' : channel.id === 'receptionist' ? 'View calls & texts' : channel.id === 'dispatcher-calls' || channel.id === 'tech-line' ? 'View calls & recordings' : channel.id === 'web-agent' || channel.id === 'customer-chats' ? 'View conversations' : 'View bookings'}</span>
        </button>;
      })}
    </div>
  </section>;
}

export function CommunicationIntegrationNeeds({ source }: { source: CommunicationSource }) {
  const channel = communicationSources.find(item => item.id === source)!;
  return <details className="comms-source-needs"><summary>{source === 'receptionist' ? 'Twilio & AI integration needed' : 'Integration needed'}</summary><ul>{channel.requirements.map(requirement => <li key={requirement}>{requirement}</li>)}</ul></details>;
}

export function CommunicationRevenue({ source }: { source: CommunicationSource }) {
  return <section className="comms-revenue" aria-labelledby="comms-revenue-title">
    <header className="comms-section-heading">
      <div><h2 id="comms-revenue-title">Revenue from communications</h2><p>Follow each source from conversation to booking, job, and payment.</p></div>
      <span className="comms-integration-status">Needs integration</span>
    </header>
    <div className="comms-revenue-table">
      <table>
        <caption className="sr-only">Revenue attribution is not connected. All amounts are unavailable.</caption>
        <thead><tr><th>Booking source</th><th>Jobs booked</th><th>Sold</th><th>Invoiced</th><th>Collected</th></tr></thead>
        <tbody>{communicationSources.filter(channel => channel.id === source).map(channel => <tr key={channel.id}><th scope="row">{channel.title}</th>{['booked', 'sold', 'invoiced', 'collected'].map(metric => <td key={metric}><span aria-label="Not connected">—</span></td>)}</tr>)}</tbody>
      </table>
    </div>
    <p className="comms-revenue-note">Revenue will appear after source tracking is connected. Sold work, invoiced amounts, and collected payments will be shown separately, with each job counted once.</p>
  </section>;
}
